"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "motion/react";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { motion, useAnimation } from "motion/react";
import { cn } from "@/lib/utils";
export interface DownloadIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface DownloadIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}
const ARROW_VARIANTS: Variants = {
  normal: {
    y: 0
  },
  animate: {
    y: 2,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 10,
      mass: 1
    }
  }
};
const DownloadIcon = forwardRef<DownloadIconHandle, DownloadIconProps>((t0, ref) => {
  const $ = _c(30);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? 28 : t1;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t2;
  if ($[6] !== controls) {
    t2 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[6] = controls;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[8] !== controls || $[9] !== onMouseEnter) {
    t3 = e => {
      if (!isControlledRef.current) {
        controls.start("animate");
      } else {
        onMouseEnter?.(e);
      }
    };
    $[8] = controls;
    $[9] = onMouseEnter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMouseEnter = t3;
  let t4;
  if ($[11] !== controls || $[12] !== onMouseLeave) {
    t4 = e_0 => {
      if (!isControlledRef.current) {
        controls.start("normal");
      } else {
        onMouseLeave?.(e_0);
      }
    };
    $[11] = controls;
    $[12] = onMouseLeave;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn(className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <polyline points="7 10 12 15 17 10" />;
    t8 = <line x1="12" x2="12" y1="15" y2="3" />;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  let t9;
  if ($[19] !== controls) {
    t9 = <motion.g variants={ARROW_VARIANTS} animate={controls}>{t7}{t8}</motion.g>;
    $[19] = controls;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== size || $[22] !== t9) {
    t10 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t6}{t9}</svg>;
    $[21] = size;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== handleMouseEnter || $[25] !== handleMouseLeave || $[26] !== props || $[27] !== t10 || $[28] !== t5) {
    t11 = <div className={t5} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t10}</div>;
    $[24] = handleMouseEnter;
    $[25] = handleMouseLeave;
    $[26] = props;
    $[27] = t10;
    $[28] = t5;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
});
DownloadIcon.displayName = "DownloadIcon";
export { DownloadIcon };